import { useEffect, useState } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  FolderOpen, 
  Users,
  Settings as SettingsIcon,
  Plus,
  LogOut,
  Menu,
  X,
  ChevronRight,
  ClipboardList,
  Camera
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_FULL = "https://customer-assets.emergentagent.com/job_trialmate/artifacts/5oiym99b_stryde-fin-01.jpg";
const LOGO_ICON = "https://customer-assets.emergentagent.com/job_trialmate/artifacts/nro02b63_stryde-fin-02.jpg";

const navItems = [
  { path: '/', label: 'Dashboard', icon: LayoutDashboard },
  { path: '/sessions', label: 'Sessions', icon: Camera },
  { path: '/tracker', label: 'Tracker', icon: ClipboardList },
  { path: '/commitments', label: 'Commitments', icon: Users },
  { path: '/documents', label: 'Documents', icon: FolderOpen },
  { path: '/settings', label: 'Settings', icon: SettingsIcon },
];

export default function Layout({ userEmail, onLogout }) {
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // Auto-close mobile menu when the route changes so a stuck
  // overlay can never swallow clicks (e.g. Settings link).
  // (`setMobileMenuOpen` is a stable React setter — included to satisfy
  // exhaustive-deps without causing extra renders.)
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname, setMobileMenuOpen]);

  const NavContent = () => (
    <>
      {/* Logo */}
      <div className="logo-container">
        <Link to="/" className="block">
          <img 
            src={LOGO_FULL} 
            alt="Stryde Research" 
            className="h-12 w-auto object-contain"
          />
        </Link>
        <p className="text-xs text-slate-500 mt-2 font-medium">Feasibility Copilot</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4 space-y-1">
        {navItems.map((item) => {
          const isActive = location.pathname === item.path;
          const Icon = item.icon;
          return (
            <Link
              key={item.path}
              to={item.path}
              onClick={() => setMobileMenuOpen(false)}
              className={`sidebar-item ${isActive ? 'active' : ''}`}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
            >
              <Icon className="w-5 h-5" strokeWidth={1.5} />
              <span className="font-medium">{item.label}</span>
              {isActive && <ChevronRight className="w-4 h-4 ml-auto" />}
            </Link>
          );
        })}
      </nav>

      {/* User section */}
      <div className="p-4 border-t border-slate-100">
        <div className="flex items-center gap-3 mb-3">
          <div className="w-8 h-8 rounded-full bg-[#2D7DD2] flex items-center justify-center text-white text-sm font-medium">
            {userEmail?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-900 truncate">{userEmail}</p>
            <p className="text-xs text-slate-500">Admin</p>
          </div>
        </div>
        <Button 
          variant="ghost" 
          className="w-full justify-start text-slate-600 hover:text-red-600 hover:bg-red-50"
          onClick={onLogout}
          data-testid="logout-btn"
        >
          <LogOut className="w-4 h-4 mr-2" />
          Sign Out
        </Button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="bg-white shadow-md"
          data-testid="mobile-menu-btn"
        >
          {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile menu overlay */}
      {mobileMenuOpen && (
        <div 
          className="mobile-menu-overlay"
          onClick={() => setMobileMenuOpen(false)}
        />
      )}

      {/* Mobile menu */}
      <div className={`mobile-menu ${mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <NavContent />
      </div>

      {/* Desktop sidebar */}
      <aside className="sidebar">
        <NavContent />
      </aside>

      {/* Main content */}
      <main className="md:ml-64 min-h-screen">
        <div className="p-6 md:p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
